import { Injectable } from '@angular/core';
import { InterpolationParameters, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, of } from 'rxjs';
import { first, switchMap } from 'rxjs/operators';
import { registerLocaleData } from '@angular/common';
import localeUK from '@angular/common/locales/uk';
import localeEn from '@angular/common/locales/en';
import { StoreKeysMainEnum, StoreKeysSubEnum } from '../enums/store-keys.enum';
import { DeviceInfoService } from './device-info.service';
import { StorageService } from './storage.service';
import { LanguagesEnum } from '../enums/langs.enum';

@Injectable({ providedIn: 'root' })
export class TranslateCommonService {
    public translateChanges$ = new BehaviorSubject<string | null>(null);
    public translateChangesLocale$ = new BehaviorSubject<string>('uk-UK');
    constructor(
        private translate: TranslateService,
        private storageService: StorageService,
        private deviceService: DeviceInfoService,
    ) {}

    public setTranslateLang(lang: string) {
        this.translate.setDefaultLang(lang);
        this.translate
            .use(lang)
            .pipe(
                first(),
                switchMap(() => {
                    return this.storageService.setSubItem(StoreKeysSubEnum.APP_LANG, lang, StoreKeysMainEnum.SETTINGS);
                }),
            )
            .subscribe(() => {
                if (lang === LanguagesEnum.UK) {
                    import('@angular/common/locales/uk').then(() => {
                        registerLocaleData(localeUK);
                    });
                    this.translateChangesLocale$.next('uk-UK');
                }
                if (lang === LanguagesEnum.EN) {
                    import('@angular/common/locales/en').then(() => {
                        registerLocaleData(localeEn);
                    });
                    this.translateChangesLocale$.next('en-US');
                }
                this.translateChanges$.next(lang);
            });
    }

    public getTranslateLang() {
        return this.translate.currentLang;
    }

    public instant(value: string | string[], interpolateParams?: InterpolationParameters) {
        return this.translate.instant(value, interpolateParams);
    }

    public hasTranslation(translationKey: string): boolean {
        let isTranslated = true;
        if (this.translate.instant(translationKey) === translationKey) {
            isTranslated = false;
        }

        return isTranslated;
    }

    public initCurrentLang() {
        this.storageService
            .getSubItem<string>(StoreKeysMainEnum.SETTINGS, StoreKeysSubEnum.APP_LANG)
            .pipe(
                first(),
                switchMap((lang) => {
                    return !lang
                        ? this.deviceService.getDeviceLangCode().pipe(
                              switchMap((data) => {
                                  let langDevice = LanguagesEnum.UK;

                                  switch (data.value) {
                                      case LanguagesEnum.EN:
                                          langDevice = LanguagesEnum.EN;
                                          break;
                                      default:
                                          langDevice = LanguagesEnum.UK;
                                          break;
                                  }

                                  return of(langDevice);
                              }),
                          )
                        : of(lang);
                }),
            )
            .subscribe((lang) => {
                this.setTranslateLang(lang);
            });
    }
}
